// v2
import React, { useState } from 'react'
import {
  MapPin, TrendingUp, ChevronDown, ChevronUp, Calculator,
  Lightbulb, ArrowRight, ArrowLeftRight, CheckCircle, XCircle,
  Briefcase, Star, Trophy, ChevronRight, Zap, Globe
} from 'lucide-react'

// ── 직업 스토리 시나리오 데이터 ───────────────────────────────────────────────
const SCENARIOS = [
  {
    id: 'developer',
    emoji: '🎮',
    title: '게임 개발자',
    category: 'IT',
    color: 'from-indigo-400 to-blue-500',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    badge: 'bg-indigo-100 text-indigo-700',
    story: `중학교 1학년 지민이는 모바일 게임을 직접 만들고 싶습니다.
게임에서 유저가 골드 아이템을 하나 살 때마다 개발자에게 3원이 들어옵니다.
초기 서버 운영비로 이미 5,000원이 지출된 상태입니다.
"총 수익이 20,000원이 되려면 아이템을 몇 개나 팔아야 하지?"
지민이는 방정식을 세워 계산하기로 했습니다.`,
    situation: '총 수익 = (아이템 1개당 3원) × 판매 수 + 초기 수익 5,000원. 목표 20,000원 달성 조건.',
    question: '총 수익이 20,000원이 되려면 아이템을 몇 개 팔아야 할까요?',
    xLabel: '아이템 판매 수 (개)',
    yLabel: '총 수익 (원)',
    slope: 3,
    intercept: 5000,
    slopeDesc: '아이템 1개 팔 때마다 +3원',
    interceptDesc: '초기 기본 수익 5,000원',
    equation: '3x + 5000 = 20000',
    answer: '3x = 20000 − 5000 = 15000\nx = 15000 ÷ 3 = 5000개',
    whyEquation: '목표 수익이 정해지면 → "= 20000"으로 방정식 완성! 이항으로 x를 구하는 전형적 패턴.',
    insight: '게임 개발자·앱 개발자는 수익 목표를 세울 때 항상 방정식을 사용합니다. "몇 개 팔면 목표 달성?" = 일차방정식!',
    realAnswer: 5000,
    unit: '개',
    tableData: [
      { x: 0, y: 5000 }, { x: 1000, y: 8000 }, { x: 2000, y: 11000 },
      { x: 3000, y: 14000 }, { x: 4000, y: 17000 }, { x: 5000, y: 20000 },
    ],
    quiz: [
      { q: '아이템 3,000개 팔았을 때 총 수익은?', a: '14,000원', hint: '3×3000 + 5000 = 9000+5000 = 14000' },
      { q: '총 수익 14,000원이 되려면 몇 개 팔아야 하나요?', a: '3,000개', hint: '3x+5000=14000 → 3x=9000 → x=3000' },
    ],
    jobs: ['게임 개발자', '앱 개발자', 'IT 스타트업 창업자', '제품 기획자'],
  },
  {
    id: 'trainer',
    emoji: '🏃',
    title: '스포츠 트레이너',
    category: '스포츠',
    color: 'from-emerald-400 to-teal-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    badge: 'bg-emerald-100 text-emerald-700',
    story: `프로 스포츠 트레이너 수진씨는 선수의 체중 관리를 담당합니다.
선수의 현재 체중은 75kg이고, 훈련을 통해 매주 0.5kg씩 감량하고 있습니다.
감량 목표는 68kg입니다.
"몇 주 훈련하면 목표 체중에 도달할까?"
수진씨는 일차방정식으로 계획을 세웁니다.`,
    situation: '현재 체중 75kg에서 매주 0.5kg 감량. 목표 체중 68kg 도달 시점 계산.',
    question: '몇 주 훈련하면 목표 체중 68kg에 도달할 수 있을까요?',
    xLabel: '훈련 주 수 (주)',
    yLabel: '체중 (kg)',
    slope: -0.5,
    intercept: 75,
    slopeDesc: '주당 −0.5kg 감량 (변화율)',
    interceptDesc: '현재 체중 75kg (초기값)',
    equation: '75 − 0.5x = 68',
    answer: '−0.5x = 68 − 75 = −7\nx = (−7) ÷ (−0.5) = 14주',
    whyEquation: '"변화량 × 횟수 + 초기값 = 목표" → 일차방정식의 전형 패턴! 이항하면 x = 목표에 필요한 횟수.',
    insight: '스포츠 트레이너·영양사·의사는 목표 달성 기간을 계산할 때 일차방정식을 사용합니다. 선수 관리의 핵심 도구!',
    realAnswer: 14,
    unit: '주',
    tableData: [
      { x: 0, y: 75 }, { x: 2, y: 74 }, { x: 4, y: 73 },
      { x: 7, y: 71.5 }, { x: 10, y: 70 }, { x: 14, y: 68 },
    ],
    quiz: [
      { q: '10주 후 체중은 몇 kg인가요?', a: '70kg', hint: '75 − 0.5×10 = 75−5 = 70' },
      { q: '체중이 70kg이 되는 시점은 몇 주 후인가요?', a: '10주', hint: '75−0.5x=70 → 0.5x=5 → x=10' },
    ],
    jobs: ['스포츠 트레이너', '영양사', '헬스케어 컨설턴트', '물리치료사'],
  },
  {
    id: 'chef',
    emoji: '👨‍🍳',
    title: '셰프·식당 운영자',
    category: '요리·경영',
    color: 'from-orange-400 to-amber-500',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    badge: 'bg-orange-100 text-orange-700',
    story: `서울 이탈리안 레스토랑을 운영하는 셰프 민준씨.
파스타 1인분을 팔면 수익이 8,000원, 재료비는 2,500원입니다.
하루 고정 비용(임대료·인건비 등)이 15,000원 들어갑니다.
"오늘 순이익이 100,000원이 되려면 몇 인분을 팔아야 할까?"
민준씨는 방정식으로 목표 판매량을 계산합니다.`,
    situation: '순이익 = (8000 − 2500) × 판매량 − 고정비용 15,000 = 목표 100,000원.',
    question: '순이익 100,000원을 달성하려면 파스타를 몇 인분 팔아야 할까요?',
    xLabel: '판매 인분 수',
    yLabel: '순이익 (원)',
    slope: 5500,
    intercept: -15000,
    slopeDesc: '1인분당 순이익 +5,500원 (8000−2500)',
    interceptDesc: '고정 비용 −15,000원',
    equation: '5500x − 15000 = 100000',
    answer: '5500x = 100000 + 15000 = 115000\nx = 115000 ÷ 5500 ≈ 20.9 → 21인분',
    whyEquation: '"수익 − 비용 = 목표" → 등호 조건 완성! 이항하면 판매량을 한 번에 계산. 모든 사업의 핵심 방정식!',
    insight: '식당 운영자·카페 창업자·유통업 종사자는 손익분기점과 목표 매출을 일차방정식으로 계산합니다.',
    realAnswer: 21,
    unit: '인분',
    tableData: [
      { x: 0, y: -15000 }, { x: 5, y: 12500 }, { x: 10, y: 40000 },
      { x: 15, y: 67500 }, { x: 20, y: 95000 }, { x: 21, y: 100500 },
    ],
    quiz: [
      { q: '15인분 팔았을 때 순이익은?', a: '67,500원', hint: '5500×15−15000 = 82500−15000 = 67500' },
      { q: '손익분기점(순이익=0)은 몇 인분인가요?', a: '약 3인분', hint: '5500x=15000 → x≈2.7 → 3인분부터 이익' },
    ],
    jobs: ['셰프', '식당 운영자', '카페 창업자', '원가 분석가'],
  },
  {
    id: 'scientist',
    emoji: '🔬',
    title: '화학 연구원',
    category: '과학',
    color: 'from-violet-400 to-purple-500',
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    badge: 'bg-violet-100 text-violet-700',
    story: `화학 연구원 준혁씨는 실험실에서 소금물을 만들고 있습니다.
10% 소금물 200g이 있는데, 여기에 물을 추가해서 4% 소금물로 만들어야 합니다.
소금의 양은 변하지 않는다는 사실을 이용해야 합니다.
"물을 몇 g 더 추가해야 할까?"
혼합 전후 소금 양이 같다는 조건이 방정식이 됩니다.`,
    situation: '10% 소금물 200g의 소금 = 20g. 물 xg 추가 후 4% 소금물 → 소금 20g 유지.',
    question: '4% 소금물을 만들려면 물을 몇 g 더 추가해야 할까요?',
    xLabel: '추가할 물의 양 (g)',
    yLabel: '소금물 농도 (%)',
    slope: -0.04,
    intercept: 20,
    slopeDesc: '물 1g 추가 → 분모 증가 → 농도 감소',
    interceptDesc: '소금 양 20g 고정 (변하지 않음)',
    equation: '20 ÷ (200 + x) × 100 = 4  →  0.04x = 12',
    answer: '20 = 4% × (200 + x) → 20 = 0.04(200+x)\n0.04x = 20 − 8 = 12\nx = 12 ÷ 0.04 = 300g',
    whyEquation: '"혼합 전후 소금 양 보존" → 등호 성립! 일차방정식으로 배합 비율을 정확히 역산할 수 있습니다.',
    insight: '화학자·약학자·식품과학자·환경공학자는 농도·혼합 문제를 매일 일차방정식으로 풀어냅니다.',
    realAnswer: 300,
    unit: 'g',
    tableData: [
      { x: 0, y: 10 }, { x: 100, y: 6.67 }, { x: 200, y: 5 },
      { x: 250, y: 4.44 }, { x: 300, y: 4 }, { x: 400, y: 3.33 },
    ],
    quiz: [
      { q: '물을 200g 추가하면 농도는 몇 %가 되나요?', a: '약 5%', hint: '20÷(200+200)×100 = 20÷400×100 = 5%' },
      { q: '농도를 5%로 만들려면 물을 몇 g 추가해야 하나요?', a: '200g', hint: '20÷(200+x)×100=5 → 200+x=400 → x=200' },
    ],
    jobs: ['화학 연구원', '약학자', '식품 과학자', '환경 공학자'],
  },
  {
    id: 'logistics',
    emoji: '🚚',
    title: '물류·배달 운영자',
    category: '물류·IT',
    color: 'from-amber-400 to-yellow-500',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    badge: 'bg-amber-100 text-amber-700',
    story: `배달 스타트업을 운영하는 수아씨.
배달 1건당 수익은 1,200원이고, 비용(연료·보험 등)은 500원입니다.
하루 고정 운영비가 30,000원 들어갑니다.
"하루에 배달 몇 건을 해야 손익분기점(수익=비용)에 도달할까?"
수아씨는 방정식으로 최소 운영 목표를 계산합니다.`,
    situation: '순이익 = (1200 − 500) × 배달 건수 − 고정비 30,000 = 0 (손익분기점)',
    question: '하루 몇 건을 배달해야 손익분기점에 도달할 수 있을까요?',
    xLabel: '배달 건수 (건)',
    yLabel: '순이익 (원)',
    slope: 700,
    intercept: -30000,
    slopeDesc: '1건당 순이익 +700원 (1200−500)',
    interceptDesc: '고정 운영비 −30,000원',
    equation: '700x − 30000 = 0',
    answer: '700x = 30000\nx = 30000 ÷ 700 ≈ 42.9 → 43건',
    whyEquation: '"수익 = 비용"이라는 조건 → 0으로 놓으면 손익분기점! 모든 사업의 생존 기준을 방정식으로 계산합니다.',
    insight: '물류 운영자·배달 플랫폼·소상공인은 손익분기점을 일차방정식으로 계산합니다. 창업의 첫 번째 수학!',
    realAnswer: 43,
    unit: '건',
    tableData: [
      { x: 0, y: -30000 }, { x: 10, y: -23000 }, { x: 20, y: -16000 },
      { x: 30, y: -9000 }, { x: 40, y: -2000 }, { x: 43, y: 100 },
    ],
    quiz: [
      { q: '30건 배달했을 때 순이익(손실)은?', a: '−9,000원 (손실)', hint: '700×30−30000 = 21000−30000 = −9000' },
      { q: '하루 순이익이 7,000원이 되려면 몇 건 배달해야 하나요?', a: '53건', hint: '700x−30000=7000 → 700x=37000 → x≈52.9 → 53건' },
    ],
    jobs: ['물류 운영자', '배달 스타트업 창업자', '공급망 분석가', '비즈니스 컨설턴트'],
  },
]

// ── 직업 탐색 데이터 ──────────────────────────────────────────────────────────
const CAREERS = [
  {
    icon: '💻',
    title: '소프트웨어 엔지니어',
    example: '수익 목표 달성 판매량 계산',
    formula: '3x + 5000 = 20000  →  x = 5000',
    salary: '연 5,000만~1억원',
    color: 'from-blue-500 to-indigo-500',
  },
  {
    icon: '🏃',
    title: '스포츠 트레이너·영양사',
    example: '목표 체중 달성 훈련 기간',
    formula: '75 − 0.5x = 68  →  x = 14',
    salary: '연 3,500만~7,000만원',
    color: 'from-emerald-500 to-teal-500',
  },
  {
    icon: '👨‍🍳',
    title: '식당·카페 운영자',
    example: '목표 순이익 달성 판매량',
    formula: '5500x − 15000 = 100000',
    salary: '연 3,000만~8,000만원',
    color: 'from-amber-500 to-orange-500',
  },
  {
    icon: '🔬',
    title: '화학·약학 연구원',
    example: '혼합 농도 맞추기',
    formula: '0.04x = 12  →  x = 300',
    salary: '연 4,500만~9,000만원',
    color: 'from-violet-500 to-purple-500',
  },
  {
    icon: '🚚',
    title: '물류·유통 운영자',
    example: '손익분기점 배달 건수',
    formula: '700x − 30000 = 0  →  x ≈ 43',
    salary: '연 3,500만~7,500만원',
    color: 'from-yellow-500 to-amber-500',
  },
  {
    icon: '📊',
    title: '경영 컨설턴트',
    example: '목표 달성 기간·수량 역산',
    formula: 'ax + b = c  →  x = (c−b)/a',
    salary: '연 5,000만~1.2억원',
    color: 'from-rose-500 to-pink-500',
  },
]

// ── 도전 퀴즈 ─────────────────────────────────────────────────────────────────
const CHALLENGES = [
  {
    id: 'c1',
    emoji: '🎮',
    level: '⭐ 쉬움',
    levelColor: 'bg-emerald-100 text-emerald-700',
    question: '어떤 수 x에 5를 더하면 12가 됩니다. x의 값을 구하세요.',
    hint: 'x + 5 = 12  →  x = 12 − 5 (이항!)',
    answer: 'x = 7',
    explanation: 'x + 5 = 12 → x = 12 − 5 = 7. 등호의 한 쪽을 다른 쪽으로 이항할 때 부호가 바뀝니다!',
  },
  {
    id: 'c2',
    emoji: '💰',
    level: '⭐⭐ 보통',
    levelColor: 'bg-amber-100 text-amber-700',
    question: '한 개에 1,500원인 아이스크림을 x개 사고 2,000원짜리 봉투를 1개 사서 총 12,500원을 냈습니다. 아이스크림은 몇 개 샀나요?',
    hint: '1500x + 2000 = 12500  →  1500x = 10500',
    answer: 'x = 7개',
    explanation: '1500x + 2000 = 12500 → 1500x = 10500 → x = 7. 이항 후 양변을 1500으로 나누면 완성!',
  },
  {
    id: 'c3',
    emoji: '🏃',
    level: '⭐⭐⭐ 어려움',
    levelColor: 'bg-red-100 text-red-700',
    question: '형의 나이는 동생 나이의 2배보다 3살 많습니다. 형의 나이가 19살이라면 동생의 나이는?',
    hint: '동생 나이를 x라 하면: 2x + 3 = 19  →  2x = 16',
    answer: 'x = 8살',
    explanation: '동생 나이 x → 2x + 3 = 19 → 2x = 16 → x = 8. 말로 된 조건을 방정식으로 번역하는 것이 핵심!',
  },
  {
    id: 'c4',
    emoji: '🚂',
    level: '⭐⭐⭐ 어려움',
    levelColor: 'bg-red-100 text-red-700',
    question: '시속 60km로 달리는 기차가 터널을 완전히 통과하는 데 4분이 걸렸습니다. 기차 길이가 200m라면 터널 길이는 몇 m인가요?',
    hint: '4분 = 1/15 시간. 이동 거리 = 60 × (1/15) = 4km = 4000m. 터널+기차 = 4000m.',
    answer: '터널 길이 = 3,800m',
    explanation: '이동 거리 = 속력×시간 = 60km/h × (4/60)h = 4km = 4000m. 터널 길이 + 기차 길이(200m) = 4000m → 터널 = 3800m.',
  },
]

const CATEGORY_COLORS: Record<string, string> = {
  IT: 'bg-indigo-100 text-indigo-700',
  스포츠: 'bg-emerald-100 text-emerald-700',
  '요리·경영': 'bg-orange-100 text-orange-700',
  과학: 'bg-violet-100 text-violet-700',
  '물류·IT': 'bg-amber-100 text-amber-700',
}

// ── 계산기 컴포넌트 ───────────────────────────────────────────────────────────
type CalcMode = 'forward' | 'inverse'

interface CalcResult {
  mode: 'forward' | 'inverse'
  inputVal: number
  outputVal: number
  steps: string[]
}

function ScenarioCalculator({ scenario }: { scenario: typeof SCENARIOS[0] }) {
  const [mode, setMode] = useState<CalcMode>('forward')
  const [inputVal, setInputVal] = useState('')
  const [result, setResult] = useState<CalcResult | null>(null)
  const [userAnswer, setUserAnswer] = useState('')
  const [checked, setChecked] = useState<boolean | null>(null)

  const reset = () => { setInputVal(''); setResult(null); setUserAnswer(''); setChecked(null) }

  const handleModeChange = (m: CalcMode) => { setMode(m); reset() }

  const calculate = () => {
    const v = parseFloat(inputVal)
    if (isNaN(v)) return

    if (mode === 'forward') {
      const raw = scenario.slope * v + scenario.intercept
      const y = Math.round(raw * 100) / 100
      setResult({
        mode: 'forward',
        inputVal: v,
        outputVal: y,
        steps: [
          `공식: ${scenario.equation}`,
          `x = ${v} 대입`,
          `y = ${scenario.slope} × ${v} + (${scenario.intercept})`,
          `y = ${scenario.slope * v} + (${scenario.intercept})`,
          `y = ${y.toLocaleString()} ${scenario.unit}`,
        ],
      })
    } else {
      if (scenario.slope === 0) return
      const raw = (v - scenario.intercept) / scenario.slope
      const x = Math.round(raw * 100) / 100
      setResult({
        mode: 'inverse',
        inputVal: v,
        outputVal: x,
        steps: [
          `방정식: ${scenario.equation}`,
          `우변을 ${v.toLocaleString()}으로 설정`,
          `${scenario.slope}x = ${v} − (${scenario.intercept}) = ${v - scenario.intercept}`,
          `x = ${v - scenario.intercept} ÷ ${scenario.slope}`,
          `x = ${x.toLocaleString()} ${scenario.xLabel.split(' ')[1]?.replace(/[()]/g, '') || ''}`,
        ],
      })
    }
    setUserAnswer('')
    setChecked(null)
  }

  const checkAnswer = () => {
    if (!result) return
    const ua = parseFloat(userAnswer.replace(/,/g, ''))
    const tolerance = Math.abs(result.outputVal) * 0.02 + 1
    setChecked(Math.abs(ua - result.outputVal) <= tolerance)
  }

  const xLabelShort = scenario.xLabel.split(' ')[0]
  const yLabelShort = scenario.yLabel.split(' ')[0]

  return (
    <div className="card border-2 border-violet-100">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <Calculator size={15} className="text-violet-600" />
          직접 계산해보기
        </p>
        {/* Mode toggle */}
        <div className="flex rounded-lg overflow-hidden border border-slate-200 text-xs">
          <button
            onClick={() => handleModeChange('forward')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'forward' ? 'bg-violet-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowRight size={11} />
            {xLabelShort} → {yLabelShort}
          </button>
          <button
            onClick={() => handleModeChange('inverse')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'inverse' ? 'bg-purple-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowLeftRight size={11} />
            역산 (방정식 풀기)
          </button>
        </div>
      </div>

      {/* Equation display */}
      <div className="mb-4 bg-slate-50 rounded-xl px-4 py-3 flex items-center gap-3">
        <span className="text-xs text-slate-400 font-medium">방정식</span>
        <span className="font-mono font-bold text-violet-700 text-lg">{scenario.equation}</span>
      </div>

      {/* Input area */}
      <div className="flex gap-3 mb-4">
        <div className="flex-1">
          <label className="text-xs font-semibold text-slate-500">
            {mode === 'forward'
              ? `🔢 ${scenario.xLabel} 를 입력하세요`
              : `🎯 목표 ${scenario.yLabel} 를 입력하세요`}
          </label>
          <input
            type="number"
            value={inputVal}
            onChange={e => { setInputVal(e.target.value); setResult(null); setChecked(null) }}
            onKeyDown={e => e.key === 'Enter' && calculate()}
            placeholder="숫자를 입력하고 계산!"
            className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300 transition-all"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={calculate}
            disabled={!inputVal}
            className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all ${
              mode === 'forward'
                ? 'bg-violet-600 hover:bg-violet-700 text-white disabled:bg-slate-200 disabled:text-slate-400'
                : 'bg-purple-600 hover:bg-purple-700 text-white disabled:bg-slate-200 disabled:text-slate-400'
            }`}
          >
            계산!
          </button>
        </div>
      </div>

      {/* Result */}
      {result && (
        <div className="space-y-3">
          {/* Step-by-step working */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs font-bold text-amber-700 mb-3 flex items-center gap-1">
              📝 풀이 과정 (단계별)
            </p>
            <div className="space-y-1.5">
              {result.steps.map((step, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className={`text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    i === result.steps.length - 1
                      ? 'bg-emerald-500 text-white'
                      : 'bg-amber-200 text-amber-700'
                  }`}>{i + 1}</span>
                  <span className={`text-sm font-mono ${
                    i === result.steps.length - 1
                      ? 'font-black text-emerald-700 text-base'
                      : 'text-amber-800'
                  }`}>{step}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Big result box */}
          <div className={`rounded-xl px-5 py-4 border-2 ${
            mode === 'forward'
              ? 'bg-violet-50 border-violet-300'
              : 'bg-purple-50 border-purple-300'
          }`}>
            <p className="text-xs font-semibold text-slate-500 mb-1">
              {mode === 'forward'
                ? `${scenario.xLabel} = ${result.inputVal} 일 때`
                : `목표 ${scenario.yLabel} = ${result.inputVal.toLocaleString()} ${scenario.unit} 일 때`}
            </p>
            <div className="flex items-baseline gap-2">
              <span className={`text-3xl font-black ${mode === 'forward' ? 'text-violet-700' : 'text-purple-700'}`}>
                {result.outputVal.toLocaleString()}
              </span>
              <span className="text-base font-semibold text-slate-500">
                {mode === 'forward' ? scenario.unit : scenario.xLabel.split(' ')[1]?.replace(/[()]/g, '') || ''}
              </span>
            </div>
          </div>

          {/* Self-check */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="text-xs font-bold text-slate-600 mb-2">🧑‍🎓 답 맞춰보기 — 직접 계산한 후 입력해보세요!</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={userAnswer}
                onChange={e => { setUserAnswer(e.target.value); setChecked(null) }}
                onKeyDown={e => e.key === 'Enter' && checkAnswer()}
                placeholder={`내 답: __ ${mode === 'forward' ? scenario.unit : ''}`}
                className={`flex-1 text-sm border-2 rounded-lg px-3 py-2 focus:outline-none transition-all ${
                  checked === null ? 'border-slate-200' : checked ? 'border-emerald-400 bg-emerald-50' : 'border-red-400 bg-red-50'
                }`}
              />
              <button
                onClick={checkAnswer}
                disabled={!userAnswer}
                className="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm font-medium disabled:bg-slate-200 disabled:text-slate-400 hover:bg-slate-800 transition-colors"
              >
                확인
              </button>
            </div>
            {checked !== null && (
              <div className={`mt-2 flex items-center gap-2 text-sm font-semibold ${checked ? 'text-emerald-600' : 'text-red-600'}`}>
                {checked
                  ? <><CheckCircle size={16} /> 정답이에요! 🎉 완벽합니다!</>
                  : <><XCircle size={16} /> 아쉬워요. 풀이 과정을 다시 확인해보세요!</>}
              </div>
            )}
          </div>

          {/* Reset */}
          <button onClick={reset} className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
            ↺ 다시 입력하기
          </button>
        </div>
      )}
    </div>
  )
}

// ── 퀴즈 컴포넌트 ─────────────────────────────────────────────────────────────
function QuizSection({ quiz }: { quiz: typeof SCENARIOS[0]['quiz'] }) {
  const [revealed, setRevealed] = useState<Record<number, boolean>>({})
  const [showHint, setShowHint] = useState<Record<number, boolean>>({})

  return (
    <div className="space-y-3">
      {quiz.map((q, i) => (
        <div key={i} className="border border-slate-200 rounded-xl p-4 bg-white">
          <p className="text-sm font-semibold text-slate-800 mb-2">❓ {q.q}</p>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowHint(h => ({ ...h, [i]: !h[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
            >
              <Lightbulb size={11} /> 힌트
            </button>
            <button
              onClick={() => setRevealed(r => ({ ...r, [i]: !r[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
            >
              {revealed[i] ? '정답 숨기기' : '정답 보기'}
            </button>
          </div>
          {showHint[i] && (
            <div className="mt-2 bg-amber-50 rounded-lg px-3 py-2 text-xs text-amber-700">
              💡 힌트: <span className="font-mono">{q.hint}</span>
            </div>
          )}
          {revealed[i] && (
            <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              <span className="text-sm font-bold text-emerald-700">✅ 정답: {q.a}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── 챌린지 카드 컴포넌트 ──────────────────────────────────────────────────────
function ChallengeCard({ ch }: { ch: typeof CHALLENGES[0] }) {
  const [showHint, setShowHint] = useState(false)
  const [revealed, setRevealed] = useState(false)

  return (
    <div className="card border border-slate-200">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{ch.emoji}</span>
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${ch.levelColor}`}>{ch.level}</span>
        </div>
      </div>
      <p className="text-sm text-slate-800 font-medium leading-relaxed mb-3">{ch.question}</p>
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setShowHint(h => !h)}
          className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
        >
          <Lightbulb size={11} /> 힌트
        </button>
        <button
          onClick={() => setRevealed(r => !r)}
          className="text-xs px-3 py-1.5 rounded-full bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors flex items-center gap-1"
        >
          <Trophy size={11} /> {revealed ? '정답 숨기기' : '정답 확인'}
        </button>
      </div>
      {showHint && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
          💡 {ch.hint}
        </div>
      )}
      {revealed && (
        <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-2">
          <p className="text-sm font-bold text-emerald-700">✅ 정답: {ch.answer}</p>
          <p className="text-xs text-emerald-600 leading-relaxed">{ch.explanation}</p>
        </div>
      )}
    </div>
  )
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────
type Tab = 'scenarios' | 'careers' | 'challenges'

export default function A1_2RealWorldPage() {
  const [selected, setSelected] = useState(SCENARIOS[0])
  const [showTable, setShowTable] = useState(false)
  const [filterCat, setFilterCat] = useState<string>('전체')
  const [activeTab, setActiveTab] = useState<Tab>('scenarios')

  const categories = ['전체', ...Array.from(new Set(SCENARIOS.map(s => s.category)))]
  const filtered = filterCat === '전체' ? SCENARIOS : SCENARIOS.filter(s => s.category === filterCat)

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'scenarios', label: '시나리오 탐색', icon: <MapPin size={14} /> },
    { id: 'careers', label: '직업과 일차방정식', icon: <Briefcase size={14} /> },
    { id: 'challenges', label: '도전 문제', icon: <Trophy size={14} /> },
  ]

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
            <Globe size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">꿈꾸는 직업과 일차방정식</h1>
            <p className="text-sm text-slate-500">게임 개발자, 트레이너, 셰프, 연구원, 물류 운영자 — 모두 일차방정식을 활용합니다!</p>
          </div>
        </div>

        {/* Career Story Banner */}
        <div className="mt-4 rounded-2xl bg-gradient-to-r from-amber-600 to-orange-600 text-white p-5">
          <p className="font-bold text-lg mb-1">🌟 꿈의 직업에서 일차방정식을 만나다!</p>
          <p className="text-sm opacity-90 mb-3">목표가 정해지면 방정식으로 역산! 수학이 가장 강력한 의사결정 도구입니다.</p>
          <div className="grid grid-cols-4 md:grid-cols-5 gap-2">
            {[
              { icon: '🎮', job: '게임 개발자' },
              { icon: '🏃', job: '스포츠 트레이너' },
              { icon: '👨‍🍳', job: '셰프·운영자' },
              { icon: '🔬', job: '화학 연구원' },
              { icon: '🚚', job: '물류 운영자' },
            ].map(({ icon, job }) => (
              <div key={job} className="bg-white/20 rounded-xl px-2 py-2 text-center">
                <p className="text-2xl">{icon}</p>
                <p className="text-xs font-bold mt-1">{job}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-xl w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-white text-violet-700 shadow-sm'
                : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── TAB 1: 시나리오 ── */}
      {activeTab === 'scenarios' && (
        <>
          {/* Category Filter */}
          <div className="flex gap-2 mb-4 flex-wrap">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilterCat(cat)}
                className={`text-xs px-4 py-2 rounded-full font-medium transition-colors ${
                  filterCat === cat
                    ? 'bg-violet-600 text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Scenario Cards — 좌측 1열 */}
            <div className="space-y-3">
              {filtered.map(scenario => (
                <button
                  key={scenario.id}
                  onClick={() => { setSelected(scenario); setShowTable(false) }}
                  className={`w-full text-left rounded-2xl border-2 p-4 transition-all ${
                    selected.id === scenario.id
                      ? `${scenario.border} ${scenario.bg} shadow-md`
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{scenario.emoji}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-800">{scenario.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[scenario.category]}`}>
                          {scenario.category}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{scenario.situation}</p>
                      <div className="mt-1.5 font-mono text-xs text-violet-600 font-semibold">{scenario.equation}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Detail Panel — 우측 2열 */}
            <div className="lg:col-span-2 space-y-4">
              {/* Situation Card */}
              <div className={`rounded-2xl border-2 ${selected.border} ${selected.bg} p-5`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-4xl">{selected.emoji}</span>
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">{selected.title}</h2>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[selected.category]}`}>
                      {selected.category}
                    </span>
                  </div>
                </div>

                {/* Story */}
                <div className="mb-4 bg-white/70 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-bold text-slate-500 mb-2">📖 이야기로 만나는 일차방정식</p>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                    {selected.story}
                  </p>
                </div>

                <p className="text-sm font-semibold text-slate-600 mb-1">📌 수학적 상황</p>
                <p className="text-sm text-slate-700 leading-relaxed mb-3">{selected.situation}</p>

                {/* Equation Breakdown */}
                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 mb-3">📐 일차방정식으로 나타내기</p>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-2xl font-black text-violet-700 font-mono">{selected.equation}</span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-2.5">
                      <p className="text-xs text-orange-500 font-semibold">미지수 계수 (= {selected.slope})</p>
                      <p className="text-xs text-orange-700 mt-0.5">{selected.slopeDesc}</p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                      <p className="text-xs text-blue-500 font-semibold">상수항 (= {selected.intercept})</p>
                      <p className="text-xs text-blue-700 mt-0.5">{selected.interceptDesc}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Question & Answer */}
              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-2">💬 문제</p>
                <p className="text-base text-slate-800 font-medium">{selected.question}</p>
                <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                  <p className="text-sm font-bold text-emerald-700 whitespace-pre-line">✅ 풀이: {selected.answer}</p>
                </div>
                <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <p className="text-xs text-amber-700">
                    <span className="font-semibold">왜 일차방정식인가?</span> {selected.whyEquation}
                  </p>
                </div>
              </div>

              {/* Table */}
              <div className="card">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <TrendingUp size={15} className="text-violet-500" />
                    표로 보기
                  </p>
                  <button
                    onClick={() => setShowTable(!showTable)}
                    className="text-xs flex items-center gap-1 text-slate-500 hover:text-slate-700 bg-slate-100 px-3 py-1 rounded-full"
                  >
                    {showTable ? '표 접기' : '표 펼치기'}
                    {showTable ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                </div>

                {showTable && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr>
                          <th className="border border-slate-200 px-3 py-2 bg-orange-50 text-orange-700 text-left">
                            {selected.xLabel}
                          </th>
                          <th className="border border-slate-200 px-3 py-2 bg-violet-50 text-violet-700 text-left">
                            {selected.yLabel}
                          </th>
                          <th className="border border-slate-200 px-3 py-2 bg-slate-50 text-slate-600 text-left">
                            변화율 (Δy/Δx)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {selected.tableData.map((row, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="border border-slate-200 px-3 py-2 font-mono font-bold text-orange-600">{row.x}</td>
                            <td className="border border-slate-200 px-3 py-2 font-mono font-bold text-violet-700">
                              {row.y.toLocaleString()}
                            </td>
                            <td className="border border-slate-200 px-3 py-2 text-xs text-slate-500">
                              {i === 0 ? '—' : (
                                <span className="font-mono text-emerald-600">
                                  {((row.y - selected.tableData[i - 1].y) / (row.x - selected.tableData[i - 1].x)).toFixed(2)}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="text-xs text-slate-400 mt-2 text-center">
                      변화율이 항상 <span className="font-bold text-emerald-600">{selected.slope}</span>으로 일정 → 일차방정식 확인!
                    </p>
                  </div>
                )}

                {!showTable && (
                  <div className="bg-slate-50 rounded-xl p-4 text-center text-slate-500 text-sm">
                    <p className="text-2xl mb-1">📋</p>
                    <p>표 펼치기를 눌러 값의 변화를 확인해보세요!</p>
                  </div>
                )}
              </div>

              {/* Calculator */}
              <ScenarioCalculator scenario={selected} />

              {/* Quiz */}
              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-3">🧩 연습 문제</p>
                <QuizSection quiz={selected.quiz} />
              </div>

              {/* Jobs related */}
              <div className="card bg-slate-50 border border-slate-200">
                <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Briefcase size={14} className="text-violet-500" />
                  이 상황에서 일차방정식을 쓰는 직업
                </p>
                <div className="flex flex-wrap gap-2">
                  {selected.jobs.map(job => (
                    <span key={job} className="text-xs bg-violet-100 text-violet-700 px-3 py-1.5 rounded-full font-medium flex items-center gap-1">
                      <Star size={10} /> {job}
                    </span>
                  ))}
                </div>
              </div>

              {/* Insight */}
              <div className="rounded-2xl bg-gradient-to-r from-slate-800 to-slate-700 text-white p-5">
                <p className="text-sm font-bold mb-1 flex items-center gap-2">
                  <TrendingUp size={15} /> 실제로 어디서 쓰이나요?
                </p>
                <p className="text-sm text-slate-300 leading-relaxed">{selected.insight}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {['일차방정식', '이항', '등식의 성질', '역산'].map(tag => (
                    <span key={tag} className="text-xs bg-white/10 px-2 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Summary */}
          <div className="mt-8 card bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
            <h3 className="font-bold text-slate-800 mb-4 text-base">📌 일차방정식 실생활 공통 패턴</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: '🎯', title: '미지수 = 구하고 싶은 것', desc: '"무엇을 구할까?" → x로 놓는다. 목표 수량, 필요 시간, 달성 기간 등 모두 x!', color: 'text-orange-600' },
                { icon: '⚖️', title: '등호 = 같다는 조건', desc: '"수익=비용", "목표=결과" → 등호로 연결! 방정식 완성의 핵심.', color: 'text-blue-600' },
                { icon: '🔄', title: '이항하여 풀기', desc: 'ax = b → x = b ÷ a. 목표값에서 역산하면 필요한 수량이 한 번에!', color: 'text-emerald-600' },
              ].map(({ icon, title, desc, color }) => (
                <div key={title} className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-2xl mb-2">{icon}</p>
                  <p className={`font-bold text-sm ${color}`}>{title}</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── TAB 2: 직업 탐색 ── */}
      {activeTab === 'careers' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-amber-600 to-orange-500 text-white p-6">
            <h2 className="text-xl font-bold mb-2">💼 일차방정식을 사용하는 직업들</h2>
            <p className="text-sm text-amber-100">
              일차방정식은 목표가 정해졌을 때 필요한 수량을 역산하는 도구입니다.
              수천만 직업인이 매일 사용하는 핵심 기술입니다!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
            {CAREERS.map(career => (
              <div key={career.title} className="card border border-slate-200 hover:shadow-md transition-shadow">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${career.color} flex items-center justify-center text-2xl mb-3`}>
                  {career.icon}
                </div>
                <h3 className="font-bold text-slate-800 mb-1">{career.title}</h3>
                <p className="text-xs text-slate-500 mb-3 leading-relaxed">{career.example}</p>
                <div className="bg-slate-50 rounded-lg p-3 mb-3">
                  <p className="text-xs text-slate-400 mb-1">실제 사용 방정식</p>
                  <code className="text-xs font-mono text-violet-700 font-bold">{career.formula}</code>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-medium">
                    💰 {career.salary}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* AI & 미래 직업 섹션 */}
          <div className="card border border-slate-200 bg-gradient-to-br from-slate-800 to-slate-900 text-white mb-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Zap size={20} className="text-yellow-400" /> AI 시대에도 일차방정식이 필요한 이유
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                {
                  title: 'AI 모델 학습의 기본',
                  desc: '딥러닝의 선형 레이어는 y = Wx + b. 일차방정식이 AI의 구성 요소!',
                  color: 'border-yellow-400',
                },
                {
                  title: '최적화·목표 설정 문제',
                  desc: '목표 달성에 필요한 수량 계산은 언제나 일차방정식. AI 플래너도 동일 원리!',
                  color: 'border-blue-400',
                },
                {
                  title: '비즈니스 의사결정',
                  desc: '손익분기점, 목표 매출, 배달 건수 계산 모두 일차방정식이 기본 도구.',
                  color: 'border-emerald-400',
                },
                {
                  title: '공학·과학 설계',
                  desc: '목표 농도, 필요 재료량, 달성 기간 역산 등 모든 설계의 출발점.',
                  color: 'border-purple-400',
                },
              ].map(item => (
                <div key={item.title} className={`border-l-4 ${item.color} pl-4 py-2`}>
                  <p className="font-semibold text-sm mb-1">{item.title}</p>
                  <p className="text-xs text-slate-300 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* 학습 경로 */}
          <div className="card border border-violet-200 bg-violet-50">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <ChevronRight size={18} className="text-violet-600" /> 일차방정식에서 시작하는 학습 경로
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              {[
                { step: '중1', topic: '문자와 식', active: false },
                { step: '중1', topic: '일차방정식', active: true },
                { step: '중2', topic: '연립방정식', active: false },
                { step: '중3', topic: '이차방정식', active: false },
                { step: '고1', topic: '다항식·인수분해', active: false },
                { step: '고2', topic: '방정식·부등식', active: false },
                { step: '직업', topic: 'AI·데이터사이언스', active: false },
              ].map((item, i) => (
                <React.Fragment key={`${item.step}-${item.topic}`}>
                  <div className={`rounded-xl px-3 py-2 text-center ${item.active ? 'bg-violet-600 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>
                    <p className="text-xs font-bold">{item.step}</p>
                    <p className="text-xs mt-0.5">{item.topic}</p>
                  </div>
                  {i < 6 && <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 3: 도전 문제 ── */}
      {activeTab === 'challenges' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white p-6">
            <h2 className="text-xl font-bold mb-2">🏆 실생활 도전 문제</h2>
            <p className="text-sm text-amber-100">
              실제 상황에서 일차방정식을 직접 세우고 풀어보세요.
              방정식 세우기가 핵심입니다!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
            {CHALLENGES.map(ch => (
              <ChallengeCard key={ch.id} ch={ch} />
            ))}
          </div>

          {/* 핵심 공식 정리 */}
          <div className="card border border-slate-200">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Star size={16} className="text-amber-500" /> 실생활 일차방정식 풀기 비법
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  step: '① 미지수 정하기',
                  desc: '"구하고 싶은 것은?" → x로 놓는다',
                  example: '판매 개수가 궁금 → x = 판매 수',
                  color: 'bg-blue-50 border-blue-200 text-blue-800',
                },
                {
                  step: '② 방정식 세우기',
                  desc: '"같다는 조건을 등호로" → ax + b = c',
                  example: '수익 = 비용 → 방정식 완성',
                  color: 'bg-orange-50 border-orange-200 text-orange-800',
                },
                {
                  step: '③ 이항·풀기',
                  desc: '"ax = b → x = b ÷ a" → 답 완성',
                  example: '3x = 15000 → x = 5000',
                  color: 'bg-emerald-50 border-emerald-200 text-emerald-800',
                },
              ].map(item => (
                <div key={item.step} className={`border rounded-xl p-4 ${item.color}`}>
                  <p className="font-bold text-sm mb-2">{item.step}</p>
                  <p className="text-xs leading-relaxed mb-2">{item.desc}</p>
                  <div className="bg-white/60 rounded-lg px-2 py-1">
                    <p className="text-xs font-mono font-bold">{item.example}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
              <p className="text-sm font-bold text-amber-700">미지수 → 방정식 → 이항 → 답!</p>
              <p className="text-xs text-amber-500 mt-1">목표가 정해지면 방정식으로 역산 → 필요한 수량을 한 번에 계산!</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
